import { Link, usePage, router } from '@inertiajs/react'
import { useState, useEffect, useRef } from 'react'
import PageTree, { type PageNode } from './PageTree'

interface SharedWorkspace {
  id: number
  name: string
  slug: string
}

interface SidebarProps {
  workspaceName: string
  workspaceSlug: string
  pages: PageNode[]
  activePageSlug?: string
  userRole?: 'admin' | 'editor' | 'viewer'
}

function WorkspaceSwitcher({ workspaceName, workspaceSlug }: { workspaceName: string; workspaceSlug: string }) {
  const { props } = usePage<{ user_workspaces?: SharedWorkspace[] }>()
  const workspaces = props.user_workspaces ?? []
  const hasMultiple = workspaces.length > 1

  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  if (!hasMultiple) {
    return (
      <Link
        href={`/${workspaceSlug}`}
        className="truncate text-sm font-semibold text-gray-900 hover:text-indigo-600"
      >
        {workspaceName}
      </Link>
    )
  }

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex min-w-0 items-center gap-1 truncate text-sm font-semibold text-gray-900 hover:text-indigo-600"
      >
        <span className="truncate">{workspaceName}</span>
        <svg className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-200">
          {workspaces.map((ws) => (
            <button
              key={ws.id}
              onClick={() => {
                setOpen(false)
                if (ws.slug !== workspaceSlug) router.visit(`/${ws.slug}`)
              }}
              className={`block w-full truncate px-3 py-2 text-left text-sm ${
                ws.slug === workspaceSlug
                  ? 'bg-indigo-50 font-medium text-indigo-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {ws.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Sidebar({ workspaceName, workspaceSlug, pages, activePageSlug, userRole }: SidebarProps) {
  return (
    <aside className="flex h-screen w-64 flex-shrink-0 flex-col border-r border-gray-200 bg-white">
      <div className="flex h-16 items-center gap-2 border-b border-gray-200 px-4">
        <Link href={`/${workspaceSlug}`} className="flex-shrink-0 text-gray-400 hover:text-indigo-600" title="Home">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
          </svg>
        </Link>
        <WorkspaceSwitcher workspaceName={workspaceName} workspaceSlug={workspaceSlug} />
      </div>

      <div className="px-3 py-2">
        <Link
          href={`/${workspaceSlug}/search`}
          className="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Search
        </Link>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-2 pb-3">
        <PageTree
          pages={pages}
          workspaceSlug={workspaceSlug}
          activePageSlug={activePageSlug}
          userRole={userRole}
        />
      </div>
    </aside>
  )
}
